import type { CSSProperties, ReactNode } from 'react';
import { SummaryTiles, StatTile, type StatTone } from '@garnet/ui';

/** Map a Stat tone to the value colour, so SummaryTiles keep the StatStrip's tinting. */
const TONE_COLOR: Record<StatTone, string | undefined> = {
  default: undefined,
  pass: 'var(--status-pass-text)',
  warn: 'var(--status-warn-text)',
  fail: 'var(--status-fail-text)',
  accent: 'var(--garnet-text)',
};

export interface StatStripItem {
  label: ReactNode;
  value: ReactNode;
  tone?: StatTone;
}

/** A responsive grid of metric tiles — the dense internal-tool header. */
export function StatStrip({ stats }: { stats: StatStripItem[] }) {
  return (
    <SummaryTiles min={150}>
      {stats.map((s, i) => {
        const color = TONE_COLOR[s.tone ?? 'default'];
        return <StatTile key={i} label={s.label} value={s.value} {...(color ? { valueStyle: { color } } : {})} />;
      })}
    </SummaryTiles>
  );
}

/** Primary cell content: a bold name over a mono sub-line. */
export function NameCell({ name, sub }: { name: ReactNode; sub?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
      {sub && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>{sub}</span>}
    </div>
  );
}

const tagStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10.5,
  padding: '1px 6px',
  borderRadius: 4,
  border: '1px solid var(--border)',
  color: 'var(--fg-2)',
  background: 'var(--bg-sunken)',
  whiteSpace: 'nowrap',
};

/**
 * Compact mono tag list (capabilities, languages, methods). Clamps to a single line:
 * tags beyond `max` collapse into a `+N` chip so rows keep an even height.
 */
export function TagRow({ tags, max = 4 }: { tags: ReadonlyArray<string>; max?: number }) {
  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;
  return (
    <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', minWidth: 0 }}>
      {visible.map((t) => (
        <span key={t} style={tagStyle}>
          {t}
        </span>
      ))}
      {overflow > 0 && (
        <span style={{ ...tagStyle, color: 'var(--fg-3)' }} title={tags.slice(max).join(' · ')}>
          +{overflow}
        </span>
      )}
    </span>
  );
}

/** Humanise a count of seconds into a compact age string. */
export function ageLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3_600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.round(seconds / 3_600)}h`;
  return `${Math.round(seconds / 86_400)}d`;
}

/** ISO timestamp → compact UTC date-time for ops tables. */
export function dateTimeLabel(iso: string): string {
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16);
}
